<template>
  <div class="pa-4">
    <!-- Header -->
    <div class="mb-4">
      <h1 class="text-h5 font-weight-black mb-1">Indonesian Game Dev Studios</h1>
      <p class="text-body-2 text-grey">A curated directory of Indonesian game developer studios.</p>
    </div>

    <!-- Search -->
    <v-text-field
      v-model="search"
      placeholder="Search studios..."
      prepend-inner-icon="mdi-magnify"
      variant="outlined"
      density="compact"
      rounded="xl"
      clearable
      hide-details
      style="margin-bottom: 24px;"
    ></v-text-field>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <p class="mt-4 text-grey">Loading studios...</p>
    </div>

    <!-- Error -->
    <v-alert v-else-if="error" type="error" variant="tonal" class="rounded-xl border mb-4">
      {{ error }}
    </v-alert>

    <!-- Empty source list -->
    <div
      v-else-if="studios.length === 0"
      class="text-center py-16 border rounded-xl border-dashed"
      style="border-color: rgba(255,255,255,0.1) !important;"
    >
      <v-icon icon="mdi-domain" size="64" color="grey-darken-2" class="mb-4"></v-icon>
      <h3 class="text-h6 text-grey">No studios listed yet</h3>
      <p class="text-body-2 text-grey-darken-1 mt-2">Check back soon.</p>
    </div>

    <!-- No search results -->
    <div
      v-else-if="filtered.length === 0"
      class="text-center py-16 border rounded-xl border-dashed"
      style="border-color: rgba(255,255,255,0.1) !important;"
    >
      <v-icon icon="mdi-magnify-close" size="64" color="grey-darken-2" class="mb-4"></v-icon>
      <h3 class="text-h6 text-grey">No studios match "{{ search }}"</h3>
    </div>

    <!-- Grid -->
    <v-row v-else>
      <v-col
        v-for="studio in filtered"
        :key="studio.rkey"
        cols="12"
        sm="6"
        lg="4"
      >
        <v-card
          class="rounded-xl h-100 d-flex flex-column"
          style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.08);"
          flat
          hover
          @click="openDetail(studio)"
        >
          <!-- Banner + avatar area -->
          <div
            class="d-flex align-end justify-start rounded-t-xl px-3 pb-2"
            style="height: 100px; position: relative; overflow: hidden; background: rgba(255,255,255,0.03);"
          >
            <!-- Banner image -->
            <div
              v-if="studio.banner"
              style="position: absolute; inset: 0; background-size: cover; background-position: center;"
              :style="{ backgroundImage: `url(${studio.banner})` }"
            ></div>
            <!-- Dark overlay -->
            <div
              v-if="studio.banner"
              style="position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.55));"
            ></div>
            <!-- Avatar -->
            <v-avatar size="52" rounded="lg" color="surface-variant" style="position: relative; border: 2px solid rgba(255,255,255,0.15);">
              <v-img v-if="studio.logoUrl" :src="studio.logoUrl" cover></v-img>
              <v-icon v-else icon="mdi-domain" color="grey" size="28"></v-icon>
            </v-avatar>
          </div>

          <v-card-text class="pa-4 flex-grow-1 d-flex flex-column">
            <span class="text-subtitle-1 font-weight-black mb-1">{{ studio.name }}</span>
            <div v-if="studio.handle" class="text-caption text-primary mb-2">@{{ studio.handle }}</div>
            <p
              v-if="studio.description"
              class="text-body-2 text-grey-lighten-1 flex-grow-1"
              style="display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;"
            >{{ studio.description }}</p>
            <div v-else class="flex-grow-1"></div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Detail dialog -->
    <v-dialog v-model="dialog" max-width="520" scrollable>
      <v-card v-if="selected" class="rounded-xl d-flex flex-column" style="background: rgb(18,18,18); max-height: 90vh;">
        <!-- Banner header — fixed height, never shrinks -->
        <div
          class="rounded-t-xl flex-shrink-0"
          style="height: 130px; min-height: 130px; position: relative; overflow: hidden; background: rgba(255,255,255,0.03);"
        >
          <div
            v-if="selected.banner"
            style="position: absolute; inset: 0; background-size: cover; background-position: center;"
            :style="{ backgroundImage: `url(${selected.banner})` }"
          ></div>
          <div style="position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.65));"></div>
          <!-- Avatar bottom-left -->
          <div style="position: absolute; bottom: 12px; left: 16px;">
            <v-avatar size="72" rounded="xl" color="surface-variant" style="border: 2px solid rgba(255,255,255,0.2);">
              <v-img v-if="selected.logoUrl" :src="selected.logoUrl" cover></v-img>
              <v-icon v-else icon="mdi-domain" color="grey" size="36"></v-icon>
            </v-avatar>
          </div>
          <v-btn icon="mdi-close" variant="text" size="small" style="position: absolute; top: 8px; left: 8px;" @click="dialog = false"></v-btn>
          <v-btn
            v-if="selected.handle"
            append-icon="mdi-arrow-right"
            variant="flat"
            color="primary"
            size="small"
            class="rounded-pill see-profile-btn"
            style="position: absolute; top: 12px; right: 12px;"
            @click="router.push(`/profile/${selected.handle}`); dialog = false"
          >See Profile</v-btn>
        </div>

        <!-- Scrollable content -->
        <div class="overflow-y-auto flex-grow-1">
          <div class="pa-5 pb-2">
            <!-- Name + handle -->
            <h2 class="text-h6 font-weight-black mb-1">{{ selected.name }}</h2>
            <router-link
              v-if="selected.handle"
              :to="`/profile/${selected.handle}`"
              class="text-body-2 text-primary mb-3 d-inline-block text-decoration-none"
              style="opacity: 0.9;"
              @click="dialog = false"
            >@{{ selected.handle }}</router-link>
            <div v-else class="mb-3"></div>

            <!-- Description -->
            <p
              v-if="selected.description"
              class="text-body-2 text-grey-lighten-1 mb-3"
              style="white-space: pre-wrap;"
              v-html="renderRichText(selected.description, selected.descriptionFacets)"
              @click="handleRichTextClick($event, { router })"
            ></p>

            <!-- Website link -->
            <a
              v-if="selected.website"
              :href="selected.website"
              target="_blank"
              rel="noopener"
              class="text-caption text-grey-lighten-1 mb-3 d-inline-flex align-center ga-1 text-decoration-none"
              style="opacity: 0.7; word-break: break-all;"
              @click.stop
            >
              <v-icon icon="mdi-open-in-new" size="12"></v-icon>
              {{ selected.website }}
            </a>
          </div>

          <!-- Tabs -->
          <v-tabs v-model="activeTab" density="compact" class="px-3">
            <v-tab value="games" prepend-icon="mdi-gamepad-variant">Games</v-tab>
            <v-tab value="jobs" prepend-icon="mdi-briefcase">Jobs</v-tab>
          </v-tabs>
          <v-divider style="opacity: 0.1;"></v-divider>

          <div class="pa-4">
            <!-- Games tab -->
            <template v-if="activeTab === 'games'">
              <div v-if="loadingGames" class="d-flex align-center justify-center py-6">
                <v-progress-circular indeterminate color="primary" size="32" width="3"></v-progress-circular>
              </div>
              <template v-else-if="selectedGames !== null">
                <div v-if="selectedGames.length === 0" class="text-center py-8 text-grey text-body-2">No games listed.</div>
                <div v-else class="d-flex flex-column" style="gap: 10px;">
                  <div
                    v-for="(game, i) in selectedGames"
                    :key="i"
                    class="d-flex align-center ga-3 rounded-lg pa-2"
                    style="background: rgba(255,255,255,0.04);"
                  >
                    <div style="width: 56px; height: 56px; flex-shrink: 0; border-radius: 8px; overflow: hidden; background: rgba(255,255,255,0.06);">
                      <v-img v-if="game.thumbnail" :src="game.thumbnail" width="56" height="56" cover></v-img>
                      <div v-else class="d-flex align-center justify-center h-100">
                        <v-icon icon="mdi-gamepad-variant" color="grey-darken-1" size="24"></v-icon>
                      </div>
                    </div>
                    <div class="flex-grow-1 overflow-hidden">
                      <div class="text-body-2 font-weight-black text-truncate">{{ game.title }}</div>
                      <div class="text-caption text-grey">{{ [game.role, game.year, game.platform].filter(Boolean).join(' · ') }}</div>
                    </div>
                    <a v-if="game.link" :href="game.link" target="_blank" rel="noopener" @click.stop>
                      <v-icon icon="mdi-open-in-new" size="16" color="grey"></v-icon>
                    </a>
                  </div>
                </div>
              </template>
            </template>

            <!-- Jobs tab -->
            <template v-if="activeTab === 'jobs'">
              <div v-if="loadingJobs" class="d-flex align-center justify-center py-6">
                <v-progress-circular indeterminate color="primary" size="32" width="3"></v-progress-circular>
              </div>
              <div v-else-if="selectedJobs.length === 0" class="text-center py-8 text-grey text-body-2">No job posts found.</div>
              <div v-else class="d-flex flex-column" style="gap: 10px;">
                <div
                  v-for="(post, i) in selectedJobs"
                  :key="i"
                  class="rounded-lg pa-3 cursor-pointer"
                  style="background: rgba(255,255,255,0.04);"
                  @click="router.push(`/profile/${post.author.handle}/post/${post.uri.split('/').pop()}`); dialog = false"
                >
                  <div
                    class="text-body-2 text-grey-lighten-1"
                    style="display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; white-space: pre-wrap;"
                    v-html="renderRichText(post.record.text, post.record.facets)"
                  ></div>
                  <div class="text-caption text-grey mt-2" style="opacity: 0.5;">
                    {{ new Date(post.record.createdAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) }}
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Footer actions -->
        <v-divider style="opacity: 0.08;" class="flex-shrink-0"></v-divider>
        <div class="pa-3 d-flex ga-2 flex-wrap flex-shrink-0">
          <v-btn
            v-if="selected.handle"
            :href="`https://bsky.app/profile/${selected.handle}`"
            target="_blank"
            rel="noopener"
            variant="tonal"
            color="primary"
            prepend-icon="mdi-butterfly"
            class="rounded-pill"
            size="small"
          >Bluesky</v-btn>
          <v-btn
            v-if="selected.website"
            :href="selected.website"
            target="_blank"
            rel="noopener"
            variant="tonal"
            color="grey"
            prepend-icon="mdi-open-in-new"
            class="rounded-pill"
            size="small"
          >Website</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { getListMembers, getProfilesBatch, getPortfolio, searchPosts, type Studio, type GameWork } from '@/services/bluesky';
import { renderRichText, handleRichTextClick } from '@/utils/richtext';

const router = useRouter();

const STUDIO_LIST_URI = import.meta.env.VITE_GAME_STUDIO_LIST_URI as string;

const studios = ref<Studio[]>([]);
const loading = ref(true);
const error = ref('');
const search = ref('');
const dialog = ref(false);
const selected = ref<Studio | null>(null);
const activeTab = ref('games');

const selectedGames = ref<GameWork[] | null>(null);
const loadingGames = ref(false);

const selectedJobs = ref<any[]>([]);
const loadingJobs = ref(false);

const filtered = computed(() => {
  const q = search.value?.trim().toLowerCase();
  if (!q) return studios.value;
  return studios.value.filter(s =>
    s.name?.toLowerCase().includes(q) ||
    s.handle?.toLowerCase().includes(q) ||
    s.description?.toLowerCase().includes(q)
  );
});

async function fetchGames(studio: Studio) {
  if (!studio.rkey) return;
  loadingGames.value = true;
  try {
    const portfolio = await getPortfolio(studio.rkey);
    selectedGames.value = portfolio?.ludography?.length ? portfolio.ludography : [];
  } catch {
    selectedGames.value = [];
  } finally {
    loadingGames.value = false;
  }
}

async function fetchJobs(studio: Studio) {
  if (!studio.handle) return;
  loadingJobs.value = true;
  try {
    const { posts } = await searchPosts(`from:${studio.handle} #gdijob`, 20);
    selectedJobs.value = posts;
  } catch {
    selectedJobs.value = [];
  } finally {
    loadingJobs.value = false;
  }
}

async function openDetail(studio: Studio) {
  selected.value = studio;
  activeTab.value = 'games';
  selectedGames.value = null;
  selectedJobs.value = [];
  dialog.value = true;
  fetchGames(studio);
}

watch(activeTab, (tab) => {
  if (!selected.value) return;
  if (tab === 'jobs' && selectedJobs.value.length === 0 && !loadingJobs.value) {
    fetchJobs(selected.value);
  }
});

async function fetchAllMembers() {
  const results: Studio[] = [];
  let cursor: string | undefined;
  do {
    const { items, cursor: next } = await getListMembers(STUDIO_LIST_URI, 100, cursor);
    for (const item of items) {
      const p = item.subject as any;
      results.push({
        rkey: p.did,
        name: p.displayName || p.handle,
        handle: p.handle,
        description: p.description || '',
        logoUrl: p.avatar || '',
        createdAt: ''
      });
    }
    cursor = next;
  } while (cursor);
  return results;
}

onMounted(async () => {
  try {
    studios.value = (await fetchAllMembers()).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'id'));

    // Batch fetch full profiles to get banners
    const dids = studios.value.map(s => s.rkey!).filter(Boolean);
    const profiles = await getProfilesBatch(dids);
    for (const studio of studios.value) {
      if (studio.rkey && profiles[studio.rkey]) {
        const p = profiles[studio.rkey];
        studio.banner = p.banner || '';
        studio.descriptionFacets = p.descriptionFacets ?? [];
      }
    }
  } catch (e: any) {
    error.value = e?.message || 'Failed to load studios';
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.see-profile-btn {
  animation: pulse-glow 2s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.5); }
  50% { box-shadow: 0 0 0 6px rgba(var(--v-theme-primary), 0); }
}
</style>
